import { r as registerInstance, f as forceUpdate, h, g as getElement, a as getRenderingRef, H as Host } from './index-fdcaa348.js';

const template = document.createElement('template');
function parseHTML(html) {
  template.innerHTML = html;
  return template.content.cloneNode(true);
}

function getDimensionString(value) {
  if (isNaN(Number(value))) {
    return value;
  }
  return `${value}px`;
}

const apIconCss = ":host{display:inline-block}:host svg,ap-icon svg{height:var(--icon-size, 12px);width:var(--icon-size, 12px)}";

const ApIcon = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
  }
  async refresh() {
    forceUpdate(this);
  }
  updateSize(newValue) {
    const iconDimension = getDimensionString(newValue);
    if (iconDimension === undefined) {
      this.host.style.removeProperty('--icon-size');
      return;
    }
    this.host.style.setProperty('--icon-size', iconDimension);
  }
  componentDidLoad() {
    this.updateSize(this.iconSize);
  }
  render() {
    return (h("svg", null, h("use", { href: `#${this.icon}` })));
  }
  get host() { return getElement(this); }
  static get watchers() { return {
    "iconSize": ["updateSize"]
  }; }
};
ApIcon.style = apIconCss;

const appendToMap = (map, propName, value) => {
    const items = map.get(propName);
    if (!items) {
        map.set(propName, [value]);
    }
    else if (!items.includes(value)) {
        items.push(value);
    }
};
const debounce = (fn, ms) => {
    let timeoutId;
    return (...args) => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(() => {
            timeoutId = 0;
            fn(...args);
        }, ms);
    };
};

/**
 * Check if a possible element isConnected.
 * The property might not be there, so we check for it.
 *
 * We want it to return true if isConnected is not a property,
 * otherwise we would remove these elements and would not update.
 *
 * Better leak in Edge than to be useless.
 */
const isConnected = (maybeElement) => !('isConnected' in maybeElement) || maybeElement.isConnected;
const cleanupElements = debounce((map) => {
    for (let key of map.keys()) {
        map.set(key, map.get(key).filter(isConnected));
    }
}, 2000);
const stencilSubscription = () => {
    if (typeof getRenderingRef !== 'function') {
        // If we are not in a stencil project, we do nothing.
        // This function is not really exported by @stencil/core.
        return {};
    }
    const elmsToUpdate = new Map();
    return {
        dispose: () => elmsToUpdate.clear(),
        get: (propName) => {
            const elm = getRenderingRef();
            if (elm) {
                appendToMap(elmsToUpdate, propName, elm);
            }
        },
        set: (propName) => {
            const elements = elmsToUpdate.get(propName);
            if (elements) {
                elmsToUpdate.set(propName, elements.filter(forceUpdate));
            }
            cleanupElements(elmsToUpdate);
        },
        reset: () => {
            elmsToUpdate.forEach((elms) => elms.forEach(forceUpdate));
            cleanupElements(elmsToUpdate);
        },
    };
};

const createObservableMap = (defaultState, shouldUpdate = (a, b) => a !== b) => {
    let states = new Map(Object.entries(defaultState !== null && defaultState !== void 0 ? defaultState : {}));
    const handlers = {
        dispose: [],
        get: [],
        set: [],
        reset: [],
    };
    const reset = () => {
        states = new Map(Object.entries(defaultState !== null && defaultState !== void 0 ? defaultState : {}));
        handlers.reset.forEach((cb) => cb());
    };
    const dispose = () => {
        // Call first dispose as resetting the state would
        // cause less updates ;)
        handlers.dispose.forEach((cb) => cb());
        reset();
    };
    const get = (propName) => {
        handlers.get.forEach((cb) => cb(propName));
        return states.get(propName);
    };
    const set = (propName, value) => {
        const oldValue = states.get(propName);
        if (shouldUpdate(value, oldValue, propName)) {
            states.set(propName, value);
            handlers.set.forEach((cb) => cb(propName, value, oldValue));
        }
    };
    const state = (typeof Proxy === 'undefined'
        ? {}
        : new Proxy(defaultState, {
            get(_, propName) {
                return get(propName);
            },
            ownKeys(_) {
                return Array.from(states.keys());
            },
            getOwnPropertyDescriptor() {
                return {
                    enumerable: true,
                    configurable: true,
                };
            },
            has(_, propName) {
                return states.has(propName);
            },
            set(_, propName, value) {
                set(propName, value);
                return true;
            },
        }));
    const on = (eventName, callback) => {
        handlers[eventName].push(callback);
        return () => {
            removeFromArray(handlers[eventName], callback);
        };
    };
    const onChange = (propName, cb) => {
        const unSet = on('set', (key, newValue) => {
            if (key === propName) {
                cb(newValue);
            }
        });
        const unReset = on('reset', () => cb(defaultState[propName]));
        return () => {
            unSet();
            unReset();
        };
    };
    const use = (...subscriptions) => {
        const unsubs = subscriptions.reduce((unsubs, subscription) => {
            if (subscription.set) {
                unsubs.push(on('set', subscription.set));
            }
            if (subscription.get) {
                unsubs.push(on('get', subscription.get));
            }
            if (subscription.reset) {
                unsubs.push(on('reset', subscription.reset));
            }
            if (subscription.dispose) {
                unsubs.push(on('dispose', subscription.dispose));
            }
            return unsubs;
        }, []);
        return () => unsubs.forEach((unsub) => unsub());
    };
    const forceUpdate = (key) => {
        const oldValue = states.get(key);
        handlers.set.forEach((cb) => cb(key, oldValue, oldValue));
    };
    return {
        state,
        get,
        set,
        on,
        onChange,
        use,
        dispose,
        reset,
        forceUpdate,
    };
};
const removeFromArray = (array, item) => {
    const index = array.indexOf(item);
    if (index >= 0) {
        array[index] = array[array.length - 1];
        array.length--;
    }
};

const createStore = (defaultState, shouldUpdate) => {
    const map = createObservableMap(defaultState, shouldUpdate);
    map.use(stencilSubscription());
    return map;
};

const apIllustrationCss = ":host{display:inline-block;width:var(--illustration-width, auto);height:var(--illustration-height, auto)}:host img{height:100%;width:100%}";

const illustrationStore = createStore({});
const ApIllustration = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
    this.svgElement = null;
  }
  updateWidth(newValue) {
    this.host.style.setProperty('--illustration-width', getDimensionString(newValue));
  }
  updateHeight(newValue) {
    this.host.style.setProperty('--illustration-height', getDimensionString(newValue));
  }
  componentDidLoad() {
    this.updateWidth(this.width);
    this.updateHeight(this.height);
  }
  // eslint-disable-next-line class-methods-use-this
  async register(type, svg) {
    const blob = new Blob([svg], { type: 'image/svg+xml; charset=utf-8' });
    illustrationStore.state[type] = URL.createObjectURL(blob);
  }
  // eslint-disable-next-line class-methods-use-this
  render() {
    return (h(Host, null, this.type && h("img", { src: illustrationStore.state[this.type] })));
  }
  get host() { return getElement(this); }
  static get watchers() { return {
    "width": ["updateWidth"],
    "height": ["updateHeight"]
  }; }
};
ApIllustration.style = apIllustrationCss;

const ApSVGSprite = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
  }
  connectedCallback() {
    this.renderSprite(this.src);
  }
  componentWillUpdate() {
    this.renderSprite(this.src);
  }
  async renderSprite(spriteHref) {
    if (!spriteHref.endsWith('.svg')) {
      return;
    }
    try {
      const spriteContent = await fetch(spriteHref).then(r => r.text());
      this.el.appendChild(parseHTML(spriteContent));
    }
    catch (e) {
      console.error(e);
      this.el.innerText = '';
    }
  }
  // eslint-disable-next-line class-methods-use-this
  render() {
    return (h(Host, null));
  }
  get el() { return getElement(this); }
};
ApSVGSprite.style = "ap-svg-sprite svg { height: 0px; width: 0px; display: block; }";

export { ApIcon as ap_icon, ApIllustration as ap_illustration, ApSVGSprite as ap_svg_sprite };
